import React, { useState, useEffect } from 'react';
import { 
  Layout, Save, Eye, EyeOff, GripVertical, Loader2, RotateCcw, 
  Plus, Trash2, X, Edit2, Check
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const DEFAULT_SECTIONS = [
  { id: 'hero', name: 'Hero Banner', enabled: true, order: 0, isDefault: true },
  { id: 'registration', name: 'Registration CTA', enabled: true, order: 1, isDefault: true },
  { id: 'why_choose_us', name: 'Why Choose Us', enabled: true, order: 2, isDefault: true },
  { id: 'success_stories', name: 'Success Stories', enabled: true, order: 3, isDefault: true },
  { id: 'cta', name: 'Call to Action', enabled: true, order: 4, isDefault: true },
];

// Available section types that can be added
const AVAILABLE_SECTION_TYPES = [
  { id: 'custom_text', name: 'Custom Text Section', description: 'Add custom text content' },
  { id: 'featured_profiles', name: 'Featured Profiles', description: 'Show featured member profiles' },
  { id: 'statistics', name: 'Statistics Counter', description: 'Show site statistics' },
  { id: 'testimonials', name: 'Testimonials', description: 'Customer testimonials carousel' },
  { id: 'faq', name: 'FAQ Section', description: 'Frequently asked questions' },
  { id: 'newsletter', name: 'Newsletter Signup', description: 'Email subscription form' },
  { id: 'partners', name: 'Partners/Sponsors', description: 'Logo showcase' },
  { id: 'video', name: 'Video Section', description: 'Embed video content' },
  { id: 'gallery', name: 'Image Gallery', description: 'Photo gallery grid' },
  { id: 'contact', name: 'Contact Form', description: 'Quick contact section' },
];

const AdminHomepageSections = () => {
  const [sections, setSections] = useState(DEFAULT_SECTIONS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [editName, setEditName] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    loadSections();
  }, []);

  const loadSections = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/homepage-sections`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.sections && data.sections.length > 0) {
          setSections(data.sections.sort((a, b) => a.order - b.order));
        }
      }
    } catch (error) {
      console.error('Failed to load sections:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/homepage-sections`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ sections })
      });

      if (response.ok) {
        toast({
          title: "Saved!",
          description: "Homepage sections updated. Changes will appear on the website.",
        });
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save sections",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (id) => {
    setSections(sections.map(s => 
      s.id === id ? { ...s, enabled: !s.enabled } : s
    ));
  };

  const handleReset = () => {
    setSections(DEFAULT_SECTIONS);
    toast({
      title: "Reset",
      description: "Sections reset to default. Click Save to apply.",
    });
  };

  const handleAddSection = (sectionType) => {
    const newSection = {
      id: `${sectionType.id}_${Date.now()}`,
      name: sectionType.name,
      type: sectionType.id,
      enabled: true,
      order: sections.length,
      isDefault: false,
      isCustom: true
    };
    
    setSections([...sections, newSection]);
    setShowAddModal(false);
    
    toast({
      title: "Section Added",
      description: `"${sectionType.name}" added. Don't forget to save!`,
    });
  };

  const handleRemoveSection = (id) => {
    const section = sections.find(s => s.id === id);
    
    if (section?.isDefault) {
      // For default sections, just disable them
      setSections(sections.map(s => 
        s.id === id ? { ...s, enabled: false } : s
      ));
      toast({
        title: "Section Hidden",
        description: "Default sections can be hidden but not removed.",
      });
    } else {
      // For custom sections, remove completely
      setSections(sections.filter(s => s.id !== id).map((s, i) => ({ ...s, order: i })));
      toast({
        title: "Section Removed",
        description: "Section has been removed. Save to apply changes.",
      });
    }
  };

  const handleStartEdit = (section) => {
    setEditingSection(section.id);
    setEditName(section.name);
  };

  const handleSaveEdit = (id) => {
    if (editName.trim()) {
      setSections(sections.map(s => 
        s.id === id ? { ...s, name: editName.trim() } : s
      ));
    }
    setEditingSection(null);
    setEditName('');
  };

  const handleCancelEdit = () => {
    setEditingSection(null);
    setEditName('');
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === index) return;

    const newSections = [...sections];
    const draggedSection = newSections[draggedItem];
    newSections.splice(draggedItem, 1);
    newSections.splice(index, 0, draggedSection);
    
    // Update order values
    newSections.forEach((s, i) => s.order = i);
    
    setSections(newSections);
    setDraggedItem(index);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  const moveSection = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= sections.length) return;

    const newSections = [...sections];
    [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
    newSections.forEach((s, i) => s.order = i);
    setSections(newSections);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-mint-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Homepage Sections</h1>
          <p className="text-gray-600">Add, remove, show/hide, and reorder sections on your homepage</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleReset}>
            <RotateCcw className="w-4 h-4 mr-2" /> Reset
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setShowAddModal(true)}
            className="border-green-300 text-green-700 hover:bg-green-50"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Section
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-mint-500 hover:bg-mint-600">
            {saving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
            ) : (
              <><Save className="w-4 h-4 mr-2" /> Save Changes</>
            )}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Layout className="w-5 h-5" />
            Section Order
          </CardTitle>
          <p className="text-sm text-gray-500">Drag to reorder, toggle to show/hide, click trash to remove</p>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {sections.map((section, index) => (
              <div
                key={section.id}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                className={`flex items-center gap-4 p-4 bg-white border rounded-lg transition-all cursor-move ${
                  draggedItem === index ? 'opacity-50 border-mint-500' : 'hover:border-gray-300'
                } ${!section.enabled ? 'opacity-60 bg-gray-50' : ''}`}
              >
                <GripVertical className="w-5 h-5 text-gray-400 flex-shrink-0" />
                
                <div className="flex-1">
                  {editingSection === section.id ? (
                    <div className="flex items-center gap-2">
                      <Input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="h-8 w-48"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveEdit(section.id);
                          if (e.key === 'Escape') handleCancelEdit();
                        }}
                      />
                      <Button size="sm" variant="ghost" onClick={() => handleSaveEdit(section.id)}>
                        <Check className="w-4 h-4 text-green-600" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={handleCancelEdit}>
                        <X className="w-4 h-4 text-gray-500" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{section.name}</span>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="h-6 w-6 p-0 opacity-50 hover:opacity-100"
                        onClick={() => handleStartEdit(section)}
                      >
                        <Edit2 className="w-3 h-3" />
                      </Button>
                      {!section.enabled && (
                        <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">Hidden</span>
                      )}
                      {section.isCustom && (
                        <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded">Custom</span>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-gray-500">Position: {index + 1}</p>
                </div>

                {/* Move Up/Down Buttons */}
                <div className="flex flex-col gap-1">
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="h-6 px-2"
                    onClick={() => moveSection(index, 'up')}
                    disabled={index === 0}
                  >
                    ↑
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="h-6 px-2"
                    onClick={() => moveSection(index, 'down')}
                    disabled={index === sections.length - 1}
                  >
                    ↓
                  </Button>
                </div>

                <div className="flex items-center gap-2">
                  <Switch
                    checked={section.enabled}
                    onCheckedChange={() => handleToggle(section.id)}
                  />
                  {section.enabled ? (
                    <Eye className="w-4 h-4 text-green-500" />
                  ) : (
                    <EyeOff className="w-4 h-4 text-gray-400" />
                  )}
                </div>

                {/* Remove Button */}
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  onClick={() => handleRemoveSection(section.id)}
                  title={section.isDefault ? "Hide section" : "Remove section"}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>

          {sections.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Layout className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>No sections configured. Click "Add Section" to get started.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section counts */}
      <div className="flex gap-4 text-sm text-gray-600">
        <span>Total: {sections.length} sections</span>
        <span>•</span>
        <span className="text-green-600">Visible: {sections.filter(s => s.enabled).length}</span>
        <span>•</span>
        <span className="text-gray-400">Hidden: {sections.filter(s => !s.enabled).length}</span>
      </div>

      {/* Add Section Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Add New Section</h2>
                <p className="text-sm text-gray-500">Choose a section type to add to your homepage</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowAddModal(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {AVAILABLE_SECTION_TYPES.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => handleAddSection(type)}
                    className="p-4 border rounded-lg text-left hover:border-mint-500 hover:bg-mint-50 transition-all group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-mint-100 flex items-center justify-center group-hover:bg-mint-200 transition-colors">
                        <Plus className="w-5 h-5 text-mint-600" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">{type.name}</h3>
                        <p className="text-xs text-gray-500">{type.description}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="p-4 border-t bg-gray-50">
              <Button variant="outline" onClick={() => setShowAddModal(false)} className="w-full">
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminHomepageSections;
